<!-- components/ItemsDropdown.vue -->
<template>
  <div class="ad" @click.stop>

    <button ref="btnRef" class="drop-btn" :class="{ open: isOpen }" @click="toggle">
      <span class="pkg-icon">
        <i :class="'fa fa-' + icon"></i>
      </span>

      <span>{{ label }}</span>

      <span class="count-badge" :class="{ empty: !items?.length }">
        {{ items?.length ?? 0 }}
      </span>

      <i class="fa fa-chevron-down"
        :style="{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }"></i>
    </button>

    <Teleport to="body">
      <div v-if="isOpen" class="drop-menu open" :style="menuStyle" @click.stop>
        <template v-if="items?.length">
          <div class="drop-header">
            <span class="drop-title">{{ headerTitle || ('Assigned ' + label) }}</span>
            <span class="drop-count">{{ items.length }}</span>
          </div>

          <div class="asset-list">
            <div class="asset-item" v-for="item in visibleItems" :key="item.id">
              <div class="asset-icon">
                <i :class="'fa fa-' + rowIcon"></i>
              </div>
              <div class="asset-info">
                <span class="asset-name">{{ getDisplayName(item) }}</span>
                <span class="asset-serial" v-if="getSerial(item)">
                  <i class="fa fa-hashtag"></i>
                  {{ getSerial(item) }}
                </span>
              </div>
              <span v-if="statusKey" class="asset-status" v-html="getStatusBadgeHtml(item)"></span>
            </div>
          </div>

          <!-- "N more" pill -->
          <div v-if="hiddenCount > 0" class="more-pill">
            <i class="fa fa-ellipsis-h"></i>
            {{ hiddenCount }} more {{ label.toLowerCase() }}
          </div>
        </template>

        <div v-else class="empty-state">
          <div class="empty-icon"><i class="fa fa-inbox"></i></div>
          <span class="empty-text">No {{ label.toLowerCase() }} assigned</span>
          <span class="empty-sub">No linked {{ label.toLowerCase() }} for this record</span>
        </div>
      </div>
    </Teleport>

  </div>
</template>

<script>
let _uid = 0
import { getStatusBadge } from '@/utils/getStatusBadge'
export default {
  name: 'ItemsDropdown',

  props: {
    items: { type: Array, default: () => [] },
    label: { type: String, default: 'Items' },
    headerTitle: { type: String, default: '' },
    nameKey: { type: String, default: 'name' },
    nameKeys: { type: Array, default: () => [] },
    serialKey: { type: String, default: '' },
    statusKey: { type: String, default: '' },   // NEW
    icon: { type: String, default: 'cube' },
    rowIcon: { type: String, default: 'cube' },
    maxVisible: { type: Number, default: 3 },
  },

  data() {
    return {
      uid: ++_uid,
      isOpen: false,
      menuStyle: {}
    }
  },

  computed: {
    visibleItems() {
      return this.items?.slice(0, this.maxVisible) ?? []
    },
    hiddenCount() {
      return Math.max(0, (this.items?.length ?? 0) - this.maxVisible)
    }
  },

  mounted() {
    document.addEventListener('click', this.closeOnOutside)
    document.addEventListener('items-dropdown:open', this.onSiblingOpen)
    window.addEventListener('scroll', this.updatePosition, true)
    window.addEventListener('resize', this.updatePosition)
  },

  beforeUnmount() {
    document.removeEventListener('click', this.closeOnOutside)
    document.removeEventListener('items-dropdown:open', this.onSiblingOpen)
    window.removeEventListener('scroll', this.updatePosition, true)
    window.removeEventListener('resize', this.updatePosition)
  },

  methods: {
    toggle() {
      if (this.isOpen) { this.isOpen = false; return }
      document.dispatchEvent(
        new CustomEvent('items-dropdown:open', { detail: { uid: this.uid } })
      )
      this.isOpen = true
      this.$nextTick(this.updatePosition)
    },

    onSiblingOpen(e) {
      if (e.detail.uid !== this.uid) this.isOpen = false
    },

    closeOnOutside() {
      this.isOpen = false
    },

    updatePosition() {
      if (!this.$refs.btnRef) return
      const rect = this.$refs.btnRef.getBoundingClientRect()
      const menuWidth = 240
      const spaceBelow = window.innerHeight - rect.bottom
      const spaceAbove = rect.top
      const openUpward = spaceBelow < 280 && spaceAbove > spaceBelow

      let left = rect.left
      if (left + menuWidth > window.innerWidth - 8) left = window.innerWidth - menuWidth - 8

      this.menuStyle = {
        position: 'fixed',
        zIndex: 9999,
        minWidth: menuWidth + 'px',
        left: left + 'px',
        ...(openUpward
          ? { bottom: (window.innerHeight - rect.top + 8) + 'px', top: 'auto' }
          : { top: (rect.bottom + 8) + 'px', bottom: 'auto' }
        )
      }
    },

    getDisplayName(item) {
      if (this.nameKeys.length) return this.nameKeys.map(k => item[k]).filter(Boolean).join(' ')
      return item[this.nameKey] ?? '-'
    },

    getSerial(item) {
      return this.serialKey ? item[this.serialKey] : null
    },
    getStatusBadgeHtml(item) {
      if (!this.statusKey) return ''
      return getStatusBadge(item[this.statusKey])
    },
  }
}
</script>

<style>
.ad {
  position: relative;
  display: inline-block;
}

.drop-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 12px 6px 10px;
  background: #fff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #344054;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  transition: background 0.12s, border-color 0.12s, box-shadow 0.12s;
  white-space: nowrap;
}

.drop-btn:hover {
  background: #f9fafb;
  border-color: #b3b9c4;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.1);
}

.drop-btn.open {
  background: #f9fafb;
  border-color: #7f77dd;
  box-shadow: 0 0 0 3px #eeedfe;
}

.drop-btn .pkg-icon {
  width: 16px;
  height: 16px;
  border-radius: 6px;
  background: #eeedfe;
  display: flex;
  align-items: center;
  justify-content: center;
}

.drop-btn .pkg-icon .fa {
  font-size: 11px;
  color: #534ab7;
}

.asset-status {
  flex-shrink: 0;
  margin-left: auto;
}

.count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 17px;
  padding: 0 5px;
  background: #eeedfe;
  color: #3c3489;
  border: 1px solid #afa9ec;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
}

.count-badge.empty {
  background: #f2f4f7;
  color: #667085;
  border-color: #d0d5dd;
}

.drop-menu {
  background: #f1f8ff;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(16, 24, 40, 0.1), 0 2px 6px rgba(16, 24, 40, 0.06);
}

.drop-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px 8px;
  border-bottom: 1px solid #f2f4f7;
  background: #f9fafb;
}

.drop-header .drop-title {
  font-size: 11px;
  font-weight: 600;
  color: #667085;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.drop-header .drop-count {
  font-size: 11px;
  font-weight: 600;
  color: #3c3489;
  background: #eeedfe;
  border: 1px solid #afa9ec;
  padding: 1px 7px;
  border-radius: 999px;
}

.asset-list {
  padding: 6px 6px 4px;
}

.asset-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
  border-radius: 7px;
  border: 1px solid transparent;
  transition: background 0.1s, border-color 0.1s;
}

.asset-item+.asset-item {
  margin-top: 2px;
}

.asset-item:hover {
  background: #f9fafb;
  border-color: #e4e7ec;
}

.asset-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #eeedfe;
  border: 1px solid #afa9ec;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.asset-icon .fa {
  font-size: 15px;
  color: #534ab7;
}

.asset-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.asset-name {
  font-size: 13px;
  font-weight: 400;
  color: #101828;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.asset-serial {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  color: #667085;
}

/* ── "N more" pill ── */
.more-pill {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin: 0 6px 6px;
  padding: 5px 10px;
  background: #f1f5f9;
  border: 1px dashed #cbd5e1;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
}

.more-pill .fa {
  font-size: 11px;
  color: #94a3b8;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px 12px;
  gap: 6px;
  text-align: center;
}

.empty-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #f2f4f7;
  border: 1px solid #e4e7ec;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-icon .fa {
  font-size: 18px;
  color: #98a2b3;
}

.empty-text {
  font-size: 13px;
  color: #344054;
  font-weight: 500;
}

.empty-sub {
  font-size: 12px;
  color: #98a2b3;
}
</style>